import {
  SideNavV2Item,
  SideNavV2Section,
  SideNavV2WorkspaceMenuItem,
  flattenSideNavItems,
} from "@/components/interactive/SideNavV2";
import { CATEGORY_NAV_SECTION_ORDER } from "@/components/navigation/category-nav.constants";
import { PROJECT_NAVBAR_V2_SECTION_ORDER } from "@/components/navigation/ProjectNavbar.constants";
import { ProjectNavbarProps } from "@/components/navigation/ProjectNavbar.types";
import { HRefArguments } from "@/components/navigation/ProjectNavbarCommon.constants";
import { TEAM_NAVBAR_ADMIN_SECTION_ITEM_IDS } from "@/components/navigation/TeamNavbar.constants";
import { NavbarMainItem } from "@/components/navigation/navigation.types";
import { useNavLinkEnvironment } from "@/components/navigation/nav-link-environment";
import { CATEGORY_NAV_SETTINGS_ITEMS_FLAT } from "@/components/settings/category-nav-settings.constants";
import { useProjectNavbarLinks } from "@/components/navigation/useProjectNavbarLinks";
import { useTeamNavbarLinks } from "@/components/navigation/useTeamNavbarLinks";
import { ADMIN_NAVBAR_ITEM } from "@/components/navigation/admin-navbar-item";
import { AmplitudeEvent, useAmplitudeOnClick } from "@/utils/amplitude";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { useCategoryNavEnabled } from "@/utils/hooks/use-category-nav-enabled";
import { useDANGEROUSisChalkEmployee } from "@/utils/hooks/use-is-chalk-employee";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";
import {
  aliasProjectNavItemId,
  resolveSideNavPointerActiveItemId,
} from "@/components/navigation/project-nav-active-item";

const PROJECT_NAVBAR_FALLBACK_WORKSPACE_LABEL = "Chalk";
const PROJECT_NAVBAR_COLLAPSED_ROUTE_ITEM_IDS = new Set<string>(["explorer-sql", "sql-explorer"]);
const resolveNavbarHref = (
  item: NavbarMainItem<HRefArguments>,
  projectId: string | undefined,
  environmentId: string | undefined,
  context: HRefArguments,
) => {
  if (item.href == null || projectId == null || environmentId == null) return undefined;

  return item.href(projectId, environmentId, context);
};

export interface ProjectSideNavModel {
  sections: readonly SideNavV2Section<string>[];
  workspaceMenuItems: readonly SideNavV2WorkspaceMenuItem[];
  workspaceLabel: string;
  hasSideNavOverride: boolean;
  topLevelSideNavItems: readonly SideNavV2Item<string>[];
  collapsedRouteItemIds: ReadonlySet<string>;
  /** The id the current route reports, before Settings and pointer resolution. */
  rawActiveItemId: string | undefined;
  /** The row the full project sidebar highlights for the current route. */
  routeActiveItemId: string | undefined;
  settingsActive: boolean;
  /** Whether the category rail is showing Settings content that lives outside `/settings`. */
  categorySettingsActive: boolean;
  /**
   * The parent of each page that is kept off the sidebar (`hideFromNavigation`), so a route on
   * one can still find the section it belongs to.
   */
  hiddenItemParentIds: ReadonlyMap<string, string>;
}

/** Builds the project sidebar's sections and resolves which of its rows the current route is on. */
export const useProjectSideNavModel = ({
  activePage,
  activeSubPage,
  activePageV2,
  activeSubPageV2,
  sideNavOverride,
}: ProjectNavbarProps): ProjectSideNavModel => {
  const router = useRouter();
  const { activeDeploymentId, environmentId, projectId, team } = useRouteAndTeamInfo();
  const linkEnvironment = useNavLinkEnvironment();
  const projectNavbarLinks = useProjectNavbarLinks();
  const teamNavbarLinks = useTeamNavbarLinks();
  const isChalkEmployee = useDANGEROUSisChalkEmployee({ ignoreAdminMode: true });
  const canSeeAdminLinks = useDANGEROUSisChalkEmployee();
  const categoryNavEnabled = useCategoryNavEnabled();
  const sectionOrder: readonly { id: string; label: string; itemIds: readonly string[] }[] =
    categoryNavEnabled ? CATEGORY_NAV_SECTION_ORDER : PROJECT_NAVBAR_V2_SECTION_ORDER;
  const trackNavbarClick = useAmplitudeOnClick({
    event: AmplitudeEvent.PROJECT_NAVBAR_CLICK,
    component: "ProjectNavbarV2",
    href: "",
    text: "",
    description: "",
  });
  const trackGlobalNavbarClick = useAmplitudeOnClick({
    event: AmplitudeEvent.TEAM_NAVBAR_CLICK,
    component: "ProjectNavbarV2WorkspaceMenu",
    href: "",
    text: "",
    description: "",
  });
  const context = useMemo<HRefArguments>(() => ({ activeDeploymentId }), [activeDeploymentId]);

  const toSideNavItem = useCallback(
    (
      item: NavbarMainItem<HRefArguments>,
      options: {
        makeUniqueId?: boolean;
        parentId?: string;
        index?: number;
        inheritedAdmin?: boolean;
      } = {},
    ): SideNavV2Item<string> => {
      const href = resolveNavbarHref(
        item,
        linkEnvironment?.projectId,
        linkEnvironment?.environmentId,
        context,
      );
      const id = options.makeUniqueId ? `${options.parentId}:${item.id}:${options.index}` : item.id;
      const admin = options.inheritedAdmin === true || item.admin === true;

      return {
        id,
        label: item.text,
        count: item.count,
        href,
        shouldOpenInNewTab: item.shouldOpenInNewTab,
        allowNavigationWhenSelected: item.allowNavigationWhenSelected,
        icon: item.icon,
        description: item.tooltipContent,
        deprecationNotice: item.deprecationNotice,
        menuGroupLabel: item.menuGroupLabel,
        admin,
        visualTone: item.visualTone,
        disabled: item.disabled,
        childrenDisplay: item.id === "settings" ? "menu" : undefined,
        children: item.subpages
          ?.filter((subpage) => !subpage.hideFromNavigation)
          .map((subpage, index) =>
            toSideNavItem(subpage, {
              makeUniqueId: options.makeUniqueId,
              parentId: item.id,
              index,
              inheritedAdmin: admin,
            }),
          ),
        onSelect:
          href != null || item.onClick != null
            ? () => {
                trackNavbarClick({
                  href,
                  text: item.text,
                  mainPageId: options.parentId ?? item.id,
                  subPageId: options.parentId == null ? undefined : item.id,
                  projectId,
                  environmentId,
                });
                item.onClick?.();
              }
            : undefined,
      };
    },
    [
      context,
      environmentId,
      linkEnvironment?.environmentId,
      linkEnvironment?.projectId,
      projectId,
      trackNavbarClick,
    ],
  );

  const sideNavSections = useMemo(() => {
    const mainNavLinks = projectNavbarLinks.filter((item) => !item.hideFromNavigation);
    const linksById = new Map(mainNavLinks.map((item) => [item.id, item]));
    const groupedLinkIds = new Set<string>(sectionOrder.flatMap((section) => section.itemIds));
    const sections: SideNavV2Section<string>[] = sectionOrder
      .map((section) => ({
        id: section.id,
        label: section.label,
        items: section.itemIds
          .map((itemId) => linksById.get(itemId))
          .filter((item): item is NavbarMainItem<HRefArguments> => item != null)
          .map((item) => toSideNavItem(item)),
      }))
      .filter((section) => section.items.length > 0);
    const ungroupedItems = mainNavLinks
      .filter((item) => !groupedLinkIds.has(item.id))
      .map((item) => toSideNavItem(item));

    if (ungroupedItems.length > 0) {
      sections.push({
        id: "other-navigation",
        label: "Other",
        items: ungroupedItems,
      });
    }

    if (isChalkEmployee) {
      sections.push({
        id: "admin-navigation",
        label: "Admin",
        hidden: !canSeeAdminLinks,
        items: [
          toSideNavItem(ADMIN_NAVBAR_ITEM as unknown as NavbarMainItem<HRefArguments>, {
            makeUniqueId: true,
            parentId: "admin",
            index: 0,
          }),
        ],
      });
    }

    return sections;
  }, [canSeeAdminLinks, isChalkEmployee, projectNavbarLinks, sectionOrder, toSideNavItem]);

  const workspaceMenuItems = useMemo(() => {
    // The team navbar collects these into its own "Admin" menu; they are deliberately not
    // duplicated into this dropdown. They stay in the shared link list because that list is
    // also the registry TeamTopRailV2 resolves breadcrumbs from and the command palette
    // searches — dropping them from it would leave those pages with no breadcrumb label.
    const adminSectionItemIds = new Set<string>(TEAM_NAVBAR_ADMIN_SECTION_ITEM_IDS);

    return teamNavbarLinks
      .filter((link) => !adminSectionItemIds.has(link.id))
      .map((link) => {
        const href = link.href();
        const label = link.text;

        return {
          id: link.id,
          label,
          href,
          icon: link.icon,
          admin: link.admin,
          onSelect: () => {
            trackGlobalNavbarClick({
              href,
              text: label,
              mainPageId: link.id,
              projectId,
              environmentId,
            });
          },
        };
      });
  }, [environmentId, projectId, teamNavbarLinks, trackGlobalNavbarClick]);

  const hasSideNavOverride = sideNavOverride != null;
  const effectiveSideNavSections = sideNavOverride?.sections ?? sideNavSections;
  const effectiveWorkspaceMenuItems = sideNavOverride?.workspaceMenuItems ?? workspaceMenuItems;
  const effectiveWorkspaceLabel =
    sideNavOverride?.workspaceLabel ?? team?.name ?? PROJECT_NAVBAR_FALLBACK_WORKSPACE_LABEL;
  const topLevelSideNavItems = useMemo(
    () => effectiveSideNavSections.flatMap((section) => section.items),
    [effectiveSideNavSections],
  );
  const collapsedRouteItemIds = useMemo(() => {
    const collapsedItemIds = new Set(PROJECT_NAVBAR_COLLAPSED_ROUTE_ITEM_IDS);
    const settingsItem = topLevelSideNavItems.find((item) => item.id === "settings");

    if (settingsItem != null) {
      flattenSideNavItems([settingsItem]).forEach((item) => collapsedItemIds.add(item.id));
    }

    return collapsedItemIds;
  }, [topLevelSideNavItems]);
  const routeInferredActiveSubPageV2 =
    sideNavOverride == null &&
    activeSubPageV2 == null &&
    router.pathname.includes("/branches/[branchName]")
      ? "branches"
      : activeSubPageV2;
  const hasNavigationV2ActiveOverride =
    activePageV2 != null || routeInferredActiveSubPageV2 != null;
  const rawActiveItemId =
    sideNavOverride?.activeItemId ??
    routeInferredActiveSubPageV2 ??
    activePageV2 ??
    activeSubPage ??
    activePage;
  const settingsActive =
    sideNavOverride == null &&
    (activePageV2 === "settings" || (!hasNavigationV2ActiveOverride && activePage === "settings"));
  const categorySettingsActive =
    categoryNavEnabled &&
    sideNavOverride == null &&
    (settingsActive ||
      CATEGORY_NAV_SETTINGS_ITEMS_FLAT.some((pane) => pane.id === rawActiveItemId));
  const routeActiveItemId = useMemo(() => {
    const routeItemId = settingsActive ? "settings" : rawActiveItemId;
    // The category nav lists a few pages under another row's id (aliasProjectNavItemId); the
    // classic sidebar resolves route ids as reported.
    const itemId = categoryNavEnabled ? aliasProjectNavItemId(routeItemId) : routeItemId;

    return resolveSideNavPointerActiveItemId(topLevelSideNavItems, itemId) ?? itemId;
  }, [categoryNavEnabled, rawActiveItemId, settingsActive, topLevelSideNavItems]);

  const hiddenItemParentIds = useMemo(
    () =>
      new Map(
        projectNavbarLinks.flatMap((link) =>
          (link.subpages ?? [])
            .filter((subpage) => subpage.hideFromNavigation)
            .map((subpage) => [subpage.id, link.id] as const),
        ),
      ),
    [projectNavbarLinks],
  );

  return {
    sections: effectiveSideNavSections,
    workspaceMenuItems: effectiveWorkspaceMenuItems,
    workspaceLabel: effectiveWorkspaceLabel,
    hasSideNavOverride,
    topLevelSideNavItems,
    collapsedRouteItemIds,
    rawActiveItemId,
    routeActiveItemId,
    settingsActive,
    categorySettingsActive,
    hiddenItemParentIds,
  };
};
